import { useState, useContext, useEffect } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Button,
  Dialog,
  TextField,
  Typography,
  DialogContent,
  Select,
  MenuItem,
  FormControl,
  Stack,
  Checkbox,
} from "@mui/material";
//Import from project
import "../../Style/dialogstyle.scss";
import { useMutation } from "@apollo/client";
import { AuthContext } from "../../Context/AuthContext";
import { DialogTitleComp } from "../../Utils/Component";
import {
  CREATE_DISTRICT_MUNICIPALITY,
  UPDATE_DISTRICT_MUNICIPALITY,
} from "../../Schema/CapitalProvince";

export default function DistrictForm({
  open,
  editData,
  setRefetch,
  dialogTitle,
  handleClose,
  hideBackdrop,
}) {
  // Change Language
  const { t, setAlert } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const queryParams = new URLSearchParams(window.location.search);
  const provinceId = queryParams.get("provinceId");

  const [createDistrictMunicipality] = useMutation(
    CREATE_DISTRICT_MUNICIPALITY,
    {
      onCompleted: ({ createDistrictMunicipality }) => {
        setLoading(false);
        if (createDistrictMunicipality?.isSuccess === true) {
          setAlert(true, "success", createDistrictMunicipality?.message);
          handleClose();
          resetForm();
          setRefetch();
        } else {
          setAlert(true, "error", createDistrictMunicipality?.message);
        }
      },
      onError: ({ message }) => {
        setLoading(false);
        setAlert(true, "error", { messageKh: message, messageEn: message });
      },
    }
  );

  const [updateDistrictMunicipality] = useMutation(
    UPDATE_DISTRICT_MUNICIPALITY,
    {
      onCompleted: ({ updateDistrictMunicipality }) => {
        setLoading(false);
        if (updateDistrictMunicipality?.isSuccess === true) {
          setAlert(true, "success", updateDistrictMunicipality?.message);
          handleClose();
          resetForm();
          setRefetch();
        } else {
          setAlert(true, "error", updateDistrictMunicipality?.message);
        }
      },
      onError: ({ message }) => {
        setLoading(false);
        setAlert(true, "error", { messageKh: message, messageEn: message });
      },
    }
  );

  //fromik
  const CheckValidation = Yup.object().shape({
    isDefault: Yup.string().required(t("required")),
    district_municipality_type: Yup.string().required(t("required")),
    district_municipality_code: Yup.string().required(t("required")),
    district_municipality_name_en: Yup.string().required(t("required")),
    district_municipality_name_kh: Yup.string().required(t("required")),
  });

  const formik = useFormik({
    initialValues: {
      isDefault: false,
      district_municipality_type: "district",
      district_municipality_code: "",
      district_municipality_name_en: "",
      district_municipality_name_kh: "",
    },

    validationSchema: CheckValidation,
    onSubmit: async (values) => {
      setLoading(true);
      if (dialogTitle === "Create") {
        createDistrictMunicipality({
          variables: {
            input: {
              ...values,
              capical_provinces_id: provinceId,
            },
          },
        });
      } else {
        updateDistrictMunicipality({
          variables: {
            id: editData?._id,
            input: {
              ...values,
              capical_provinces_id: provinceId,
            },
          },
        });
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleSubmit,
    setFieldValue,
    getFieldProps,
    resetForm,
  } = formik;

  useEffect(() => {
    if (editData) {

      setFieldValue("isDefault", editData?.isDefault);
      setFieldValue("district_municipality_type", editData?.district_municipality_type);
      setFieldValue("district_municipality_code", editData?.district_municipality_code);
      setFieldValue("district_municipality_name_en", editData?.district_municipality_name_en);
      setFieldValue("district_municipality_name_kh", editData?.district_municipality_name_kh);
    }
  }, [editData, open]);

  return (
    <Dialog
      fullWidth
      open={open}
      maxWidth="xs"
      className="dialog-container"
      hideBackdrop={hideBackdrop}
    >
      <DialogTitleComp
        title={
          dialogTitle === "Create"
            ? t("modal_title_create_district_municipality")
            : t("modal_title_update_district_municipality")
        }
        classTitle={"dialog-title"}
        classIcon={"close-icon"}
        handleClose={handleClose}
      />

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container rowSpacing={2} columnSpacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_code")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  placeholder={t("thead_code")}
                  {...getFieldProps("district_municipality_code")}
                  error={Boolean(
                    touched.district_municipality_code &&
                    errors.district_municipality_code
                  )}
                  helperText={
                    touched.district_municipality_code &&
                    errors.district_municipality_code
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_type")}
                </Typography>
                <FormControl fullWidth className="search-field" size="small">
                  <Select {...getFieldProps("district_municipality_type")}>
                    <MenuItem value={"municipality"}>
                      {t("thead_municipality")}
                    </MenuItem>
                    <MenuItem value={"district"}>{t("thead_district")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_name")}(KH)
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  placeholder={t("thead_name")}
                  {...getFieldProps("district_municipality_name_kh")}
                  error={Boolean(
                    touched.district_municipality_name_kh &&
                    errors.district_municipality_name_kh
                  )}
                  helperText={
                    touched.district_municipality_name_kh &&
                    errors.district_municipality_name_kh
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_name")}(ENG)
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  placeholder={t("thead_name")}
                  {...getFieldProps("district_municipality_name_en")}
                  error={Boolean(
                    touched.district_municipality_name_en &&
                    errors.district_municipality_name_en
                  )}
                  helperText={
                    touched.district_municipality_name_en &&
                    errors.district_municipality_name_en
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" alignItems="center" >
                  <Checkbox
                    checked={values?.isDefault}
                    onChange={({ target }) => setFieldValue("isDefault", Boolean(target?.checked))}
                  />
                  <Typography className={t("class-name-field-title")} sx={{ marginTop: 1 }}>
                    {t("thead_default")}
                  </Typography>
                </Stack>
              </Grid>


              <Grid item xs={12}>
                {loading ? (
                  <Button className={t("class-btn-create")} fullWidth>
                    {t("loading")}
                  </Button>
                ) : (
                  <Button
                    className={t("class-btn-create")}
                    fullWidth
                    onClick={handleSubmit}
                  >
                    {dialogTitle === "Create" ? t("create") : t("update")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
